import { useEffect, useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Camera, UploadSimple, CircleNotch, X, MagicWand, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";

const ASPECT = 4 / 5;

export default function BadgePhotoEditor({ uid, open, onClose, onSaved }) {
  const [mode, setMode] = useState("pick"); // pick | camera | crop
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [enhance, setEnhance] = useState(true);
  const [removeBg, setRemoveBg] = useState(false);
  const [shirt, setShirt] = useState(false);
  const [glowup, setGlowup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [camErr, setCamErr] = useState("");
  const imgRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const reset = () => { stopStream(); setMode("pick"); setSrc(null); setCrop(null); setCamErr(""); };

  useEffect(() => {
    if (!open) reset();
    return stopStream;
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;

  const startCamera = async () => {
    setCamErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setMode("camera");
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 50);
    } catch {
      setCamErr("Camera not available — check browser permission, or use Upload instead.");
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    stopStream();
    setSrc(c.toDataURL("image/png"));
    setMode("crop");
  };

  const pickFile = (f) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { setSrc(r.result); setMode("crop"); };
    r.readAsDataURL(f);
  };

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const c = centerCrop(makeAspectCrop({ unit: "%", width: 80 }, ASPECT, width, height), width, height);
    setCrop(c);
  };

  const save = async () => {
    const img = imgRef.current;
    if (!img || !crop?.width || !crop?.height) { toast.error("Pick the crop area first"); return; }
    setBusy(true);
    try {
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const c = document.createElement("canvas");
      c.width = 840; c.height = 1050;
      const ctx = c.getContext("2d");
      if (enhance) ctx.filter = "brightness(1.07) contrast(1.1) saturate(1.05)";
      ctx.drawImage(img, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, c.width, c.height);
      const blob = await new Promise((res) => c.toBlob(res, "image/png"));
      const fd = new FormData();
      fd.append("file", new File([blob], "badge-photo.png", { type: "image/png" }));
      if (removeBg) fd.append("remove_bg", "1");
      if (shirt) fd.append("shirt", "1");
      if (glowup) fd.append("glowup", "1");
      await api.post(`/users/${uid}/documents/badge_photo`, fd);
      toast.success("Badge photo saved");
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="badge-photo-editor-modal">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <p className="font-heading text-base font-bold text-stone-900">Badge photo</p>
          <button onClick={() => { reset(); onClose?.(); }} className="text-stone-400 hover:text-stone-700" data-testid="badge-photo-cancel"><X size={18} /></button>
        </div>

        {mode === "pick" && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-stone-300 p-6 text-sm font-medium text-stone-700 hover:border-primary hover:text-primary" data-testid="badge-photo-mode-upload">
              <UploadSimple size={22} /> Upload photo
              <span className="text-xs font-normal text-stone-400">License screenshot works — crop it next</span>
              <input type="file" accept="image/*" className="hidden" data-testid="badge-photo-file-input" onChange={(e) => pickFile(e.target.files?.[0])} />
            </label>
            <button onClick={startCamera} className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-stone-300 p-6 text-sm font-medium text-stone-700 hover:border-primary hover:text-primary" data-testid="badge-photo-mode-camera">
              <Camera size={22} /> Take photo
              <span className="text-xs font-normal text-stone-400">Use this device's camera</span>
            </button>
            {camErr && <p className="col-span2 text-xs text-red-600" data-testid="badge-photo-camera-error">{camErr}</p>}
          </div>
        )}

        {mode === "camera" && (
          <div className="mt-4 space-y-3">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-black" data-testid="badge-photo-video" />
            <div className="flex gap-2">
              <Button onClick={capture} className="bg-primary text-primary-foreground" data-testid="badge-photo-capture">Capture</Button>
              <Button variant="outline" onClick={reset} data-testid="badge-photo-back">Back</Button>
            </div>
          </div>
        )}

        {mode === "crop" && src && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2" data-testid="badge-photo-zoom">
              <span className="text-xs font-semibold text-stone-500">Zoom</span>
              <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-[#6a3e97]" data-testid="badge-photo-zoom-slider" />
              <span className="w-10 text-right text-xs text-stone-500">{Math.round(zoom * 100)}%</span>
            </div>
            <div className="max-h-80 overflow-auto rounded-lg bg-stone-50">
              <ReactCrop crop={crop} onChange={setCrop} aspect={ASPECT}>
                <img src={src} alt="Crop" onLoad={onImageLoad} style={{ width: `${zoom * 100}%`, maxWidth: "none" }} data-testid="badge-photo-crop-image" />
              </ReactCrop>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-600" data-testid="badge-photo-enhance-toggle">
              <input type="checkbox" checked={enhance} onChange={(e) => setEnhance(e.target.checked)} className="accent-[#6a3e97]" />
              <MagicWand size={14} className="text-primary" /> Auto-clean (brightness & contrast)
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-600" data-testid="badge-photo-removebg-toggle">
              <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} className="accent-[#6a3e97]" />
              <Sparkle size={14} className="text-primary" /> Remove background with AI (clean studio backdrop — takes ~15s)
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-600" data-testid="badge-photo-shirt-toggle">
              <input type="checkbox" checked={shirt} onChange={(e) => setShirt(e.target.checked)} className="accent-[#6a3e97]" />
              <Sparkle size={14} className="text-primary" /> Silver Home Care uniform with AI (black polo + logo + lanyard — takes ~15s)
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-600" data-testid="badge-photo-glowup-toggle">
              <input type="checkbox" checked={glowup} onChange={(e) => setGlowup(e.target.checked)} className="accent-[#6a3e97]" />
              <Sparkle size={14} className="text-primary" /> AI glowup (light polish). Off = photo stays true to the original — only lighting and the uniform change.
            </label>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground" data-testid="badge-photo-save">
                {busy ? <><CircleNotch size={15} className="mr-1.5 animate-spin" /> Saving…</> : "Save badge photo"}
              </Button>
              <Button variant="outline" onClick={reset} data-testid="badge-photo-redo">Start over</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
